
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */



const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "button"
    }}>{`Button`}</h1>
    <p>{`A Button component is either a `}<inlineCode parentName="p">{`<button>`}</inlineCode>{` or `}<inlineCode parentName="p">{`<a>`}</inlineCode>{` (if you pass it a href prop)`}</p>
    <h3 {...{
      "id": "example"
    }}>{`Example`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live=true",
        "live": "true"
      }}>{`<View spacing={2}>
  <View>
    <Button>Confirm Purchase</Button>
  </View>
  <View maxWidth="280">
    <Button>When we have a very long text</Button>
  </View>
</View>
`}</code></pre>
    <h3 {...{
      "id": "intent"
    }}>{`Intent`}</h3>
    <p>{`Intent defines what kind of action the button is used for, it's appearance will change based on the intent. The default intent is `}<inlineCode parentName="p">{`primary`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live=true",
        "live": "true"
      }}>{`<View spacing={2}>
  <Button intent="secondary">Secondary Button</Button>
  <Button intent="destructive">Destructive</Button>
</View>
`}</code></pre>
    <h3 {...{
      "id": "variant"
    }}>{`Variant`}</h3>
    <p>{`Variants are different visual representations of a button.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live=true",
        "live": "true"
      }}>{`<View spacing={2}>
  <Button intent="secondary" variant="default">
    Configure
  </Button>
  <Button variant="outline">Accept</Button>
</View>
`}</code></pre>
    <h2 {...{
      "id": "size"
    }}>{`Size`}</h2>
    <p>{`Buttons can be one of two different sizes: `}<inlineCode parentName="p">{`small`}</inlineCode>{` and `}<inlineCode parentName="p">{`medium`}</inlineCode>{` (default).`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live=true",
        "live": "true"
      }}>{`<View spacing={2}>
  <Button size="small">Small World</Button>
  <Button>Hello World</Button>
</View>
`}</code></pre>
    <h3 {...{
      "id": "loading"
    }}>{`Loading`}</h3>
    <p>{`Buttons have a loading state to indicate to the user that their request is being processed.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live=true",
        "live": "true"
      }}>{`<View spacing={2}>
  <Button loading>Hello World</Button>
  <Button loading intent="destructive">
    Reject
  </Button>
</View>
`}</code></pre>
    <h3 {...{
      "id": "disabled"
    }}>{`Disabled`}</h3>
    <p>{`Buttons that are disabled can't be interacted with. The visual style of the button also changes to indicate to the user that the buttons are in a disabled state.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live=true",
        "live": "true"
      }}>{`<View spacing={2}>
  <Button disabled intent="secondary">
    Go back to search
  </Button>
  <Button disabled>Complete Order</Button>
</View>
`}</code></pre>
    <h3 {...{
      "id": "props---button"
    }}>{`Props - Button`}</h3>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Name`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Default Value`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`disabled`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`boolean ｜ undefined`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`n/a`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`href`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`string`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`n/a`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`intent`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`"primary" ｜ "secondary" ｜ "destructive" ｜ undefined`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`primary`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`loading`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`boolean ｜ undefined`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`n/a`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`size`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`"small" ｜ "medium" ｜ "large" ｜ undefined`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`medium`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`variant`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`"text" ｜ "default" ｜ "outline" ｜ undefined`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`default`}</inlineCode></td>
        </tr>
      </tbody>
    </table>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;